import { AlertTriangle, X, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface LicenceWarningBannerProps {
  daysUntilExpiry: number;
}

export function LicenceWarningBanner({ daysUntilExpiry }: LicenceWarningBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const isUrgent = daysUntilExpiry <= 7;

  return (
    <div
      className={`relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-b transition-colors ${
        isUrgent
          ? "bg-destructive/10 border-destructive/20 text-destructive"
          : "bg-warning/10 border-warning/20 text-amber-800 dark:text-amber-300"
      }`}
    >
      {/* Icon */}
      <AlertTriangle
        className={`w-4 h-4 shrink-0 ${isUrgent ? "text-destructive" : "text-warning"}`}
      />

      {/* Message */}
      <span className="flex-1">
        {isUrgent ? (
          <>
            <strong>Attention !</strong> Votre licence expire dans{" "}
            <strong>{daysUntilExpiry} jour{daysUntilExpiry > 1 ? "s" : ""}</strong>.
            Renouvelez immédiatement pour éviter toute interruption de service.
          </>
        ) : (
          <>
            Votre licence SMEFI expire dans{" "}
            <strong>{daysUntilExpiry} jour{daysUntilExpiry > 1 ? "s" : ""}</strong>.
            Pensez à renouveler votre abonnement avant cette date.
          </>
        )}
      </span>

      {/* CTA */}
      <Button
        size="sm"
        variant="outline"
        asChild
        className={`shrink-0 h-7 text-xs border ${
          isUrgent
            ? "border-destructive/30 hover:bg-destructive/10 text-destructive"
            : "border-warning/30 hover:bg-warning/10 text-amber-800 dark:text-amber-300"
        }`}
      >
        <a href="mailto:contact@smefi.app?subject=Renouvellement%20licence%20SMEFI">
          <Mail className="w-3 h-3 mr-1.5" />
          Contacter l'équipe
        </a>
      </Button>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className={`shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors ${
          isUrgent ? "text-destructive/70" : "text-amber-700/70 dark:text-amber-400/70"
        }`}
        aria-label="Fermer la bannière"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
